import PageHeader from "@/components/site/PageHeader";
import CTASection from "@/components/home/CTASection";
import SEO from "@/components/site/SEO";
import { motion } from "framer-motion";
import { breadcrumbSchema } from "@/lib/seo";

// Shared editorial layout for every legal / policy page.
// Sections come as an array of { n, label, body(JSX or string), highlight? }
export default function LegalPage({
  eyebrow,
  title,
  italicTail,
  intro,
  effectiveDate,
  sections = [],
  path,
  metaTitle,
  metaDescription,
  breadcrumbs,
}) {
  return (
    <>
      <SEO
        title={metaTitle || `${title} | Ragi`}
        description={metaDescription || intro}
        path={path}
        schema={
          breadcrumbs
            ? breadcrumbSchema([
                { name: "Home", path: "/" },
                ...breadcrumbs,
              ])
            : undefined
        }
      />

      <PageHeader
        eyebrow={eyebrow}
        title={title}
        italicTail={italicTail}
        intro={intro}
        meta={[
          { label: "Effective", value: effectiveDate || "[Add date]" },
          { label: "Entity", value: "Ragi Media" },
          { label: "Jurisdiction", value: "Bengaluru, India" },
        ]}
      />

      <article className="relative bg-ink-950 py-20 lg:py-28">
        <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-16">
            <aside className="lg:col-span-3">
              <div className="sticky top-28">
                <div className="font-sans text-[11px] uppercase tracking-widest text-gold">
                  Contents
                </div>
                <ol className="mt-6 space-y-3 border-t border-ivory/[0.06] pt-4">
                  {sections.map((s) => (
                    <li key={s.n} className="grid grid-cols-[36px_1fr] gap-3">
                      <span className="font-display text-lg italic text-gold">{s.n}</span>
                      <a
                        href={`#s-${s.n}`}
                        className="font-sans text-sm text-ivory hover:text-gold transition-colors"
                      >
                        {s.label}
                      </a>
                    </li>
                  ))}
                </ol>
              </div>
            </aside>

            <div className="lg:col-span-9">
              {sections.map((s, i) => (
                <motion.section
                  key={s.n}
                  id={`s-${s.n}`}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.5, delay: i * 0.03 }}
                  className="scroll-mt-28 border-b border-ivory/[0.06] py-10 first:pt-0"
                >
                  <div className="flex items-baseline gap-4">
                    <span className="font-display text-3xl italic text-gold">{s.n}</span>
                    <span className="font-sans text-[11px] uppercase tracking-widest text-ivory-muted">
                      {s.label}
                    </span>
                  </div>
                  <div className="mt-6 max-w-3xl space-y-4 font-sans text-base leading-[1.7] text-ivory-soft lg:text-lg">
                    {typeof s.body === "string" ? <p>{s.body}</p> : s.body}
                  </div>
                  {s.highlight ? (
                    <div className="mt-8 max-w-3xl border-l-2 border-gold bg-ink-900/60 p-6">
                      <p className="font-display text-lg leading-tight text-ivory lg:text-xl">
                        {s.highlight}
                      </p>
                    </div>
                  ) : null}
                </motion.section>
              ))}
            </div>
          </div>
        </div>
      </article>

      <CTASection />
    </>
  );
}
